import { englishVoices } from './osrVoice';
export type SpeechPhase = 'idle' | 'question' | 'waiting' | 'answer';
export type SpeechState = 'idle' | 'loading' | 'playing' | 'paused';
export type SpeechPort = {
  speak(text: string, lang: string, started: () => void, ended: () => void, failed: (error?: Error) => void): void;
  cancel(): void; pause(): void; resume(): void;
};

/** Independent question and answer utterances, with a cancellable, pausable thinking interval. */
export class OsrSpeechPlayer {
  phase: SpeechPhase = 'idle';
  state: SpeechState = 'idle';
  private version = 0;
  private timer?: ReturnType<typeof setTimeout>;
  private due = 0;
  private remaining = 0;
  private input?: { question: string; answer: string; lang: string; delay: number | 'manual'; finished(): void };
  constructor(private port: SpeechPort, private changed: (phase: SpeechPhase, state: SpeechState) => void,
    private reveal: () => void, private failed: () => void) {}
  private publish(state: SpeechState) { this.state = state; this.changed(this.phase, state); }
  stop() {
    this.version++; clearTimeout(this.timer); this.timer = undefined;
    this.port.cancel(); this.phase = 'idle'; this.input = undefined; this.publish('idle');
  }
  start(input: NonNullable<OsrSpeechPlayer['input']>) {
    this.stop(); this.input = input; this.say('question');
  }
  private say(phase: 'question' | 'answer') {
    if (!this.input) return;
    clearTimeout(this.timer); this.phase = phase;
    const version = this.version;
    this.publish('loading');
    if (phase === 'answer') this.reveal();
    const error = () => { if (version === this.version) { this.stop(); this.failed(); } };
    try {
      this.port.speak(this.input[phase], this.input.lang,
        () => { if (version === this.version && this.state !== 'paused') this.publish('playing'); },
        () => {
          if (version !== this.version || !this.input) return;
          if (phase === 'question') {
            this.phase = 'waiting';
            this.remaining = this.input.delay === 'manual' ? 0 : this.input.delay * 1000;
            if (this.input.delay === 'manual' || this.state === 'paused') this.publish('paused');
            else this.wait();
          } else {
            const finished = this.input.finished;
            this.phase = 'idle'; this.publish('idle'); finished();
          }
        }, error);
    } catch { error(); }
  }
  private wait() {
    this.publish('playing'); this.due = Date.now() + this.remaining;
    const version = this.version;
    this.timer = setTimeout(() => { if (version === this.version) this.say('answer'); }, this.remaining);
  }
  pause() {
    if (this.state !== 'playing' && this.state !== 'loading') return;
    if (this.phase === 'waiting') { clearTimeout(this.timer); this.remaining = Math.max(0, this.due - Date.now()); }
    else this.port.pause();
    this.publish('paused');
  }
  resume() {
    if (this.state !== 'paused') return;
    if (this.phase === 'waiting') {
      if (this.input?.delay === 'manual') this.say('answer'); else this.wait();
    } else { this.port.resume(); this.publish('playing'); }
  }
  answerNow() {
    if (this.phase !== 'waiting') return;
    this.say('answer');
  }
}

export function browserSpeechPort(preferredVoice: () => string = () => ''): SpeechPort {
  return {
    speak(text, lang, started, ended, failed) {
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = lang; utter.rate = 1;
      if(lang.startsWith('en')){const voices=englishVoices(window.speechSynthesis.getVoices());
        const selected=voices.find(voice=>voice.voiceURI===preferredVoice())||voices[0];
        if(selected){utter.voice=selected;utter.lang=selected.lang;}
      }
      utter.onstart = started; utter.onend = ended; utter.onerror = () => failed(new Error('系统语音播放失败'));
      window.speechSynthesis.speak(utter);
    },
    cancel: () => { window.speechSynthesis?.cancel(); window.speechSynthesis?.resume(); },
    pause: () => window.speechSynthesis?.pause(),
    resume: () => window.speechSynthesis?.resume(),
  };
}
